import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { systemSettingsAPI } from '../utils/api';
import { useAuth } from './AuthContext';

const DEFAULT_CONFIG = {
  orgName: 'ComDes',
  systemSubtitle: 'Studios, rooms & equipment',

  sidebarTitle: 'ComDes',
  sidebarSubtitle: 'Booking',
  loginBrandName: 'NCAD',
  loginBrandDept: 'Bookings',

  // Every portal the header can name. A saved map is merged OVER this one, so
  // a portal the setup wizard does not ask about (HoD and the support roles)
  // keeps its name instead of rendering blank.
  portalNames: {
    student: 'Student Portal',
    staff: 'Staff Portal',
    'dept-admin': 'Admin',
    'master-admin': 'Master Admin',
    hod: 'HoD',
    'view-only-staff': 'View-Only Staff',
    'accounts-officer': 'Accounts Officer',
    'it-support': 'IT Support',
  },

  customLogo: null, // base64 data URL or null (falls back to ncadLogo.gif)
};

// Keys older saves carry that nothing reads any more: the global fines switch
// (fines are per area), the room auto-approval copy (system_settings
// room_auto_approval is the one the server reads), the setup flag and the
// unread identity fields. Dropped on load so the next save does not keep them.
const RETIRED_KEYS = ['finesEnabled', 'roomAutoApproval', 'setupComplete', 'systemName', 'orgFull', 'emailDomain'];

const SystemConfigContext = createContext(null);

export function SystemConfigProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  // The settings API needs a signed-in user. Asking before sign-in only
  // produced two 401s on the login page, and the defaults were used anyway;
  // loading on sign-in also picks up the saved branding without a reload.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (userId === null) {
      setLoading(false);
      return;
    }
    loadConfig();
  }, [userId]);

  const loadConfig = async () => {
    try {
      const [configRes, logoRes] = await Promise.allSettled([
        systemSettingsAPI.getByKey('system_config'),
        systemSettingsAPI.getByKey('system_logo'),
      ]);

      let merged = { ...DEFAULT_CONFIG };

      if (configRes.status === 'fulfilled' && configRes.value?.setting?.value) {
        const saved = typeof configRes.value.setting.value === 'string'
          ? JSON.parse(configRes.value.setting.value)
          : { ...configRes.value.setting.value };
        RETIRED_KEYS.forEach((key) => delete saved[key]);
        merged = {
          ...merged,
          ...saved,
          portalNames: { ...DEFAULT_CONFIG.portalNames, ...(saved.portalNames || {}) },
        };
      }

      if (logoRes.status === 'fulfilled' && logoRes.value?.setting?.value) {
        merged.customLogo = logoRes.value.setting.value;
      }

      setConfig(merged);
    } catch {
      // Unreadable saved config: the defaults stand.
    } finally {
      setLoading(false);
    }
  };

  // Saves, THEN shows the saved values, and throws when the save is refused so
  // the caller can say so. It used to swallow its own failure, so every
  // caller's error branch was dead and a refused save reported success.
  const saveConfig = useCallback(async (newConfig, logo) => {
    const { customLogo, ...configWithoutLogo } = { ...config, ...newConfig };
    await systemSettingsAPI.update('system_config', JSON.stringify(configWithoutLogo), 'System configuration');
    if (logo !== undefined) {
      await systemSettingsAPI.update('system_logo', logo || '', 'Custom logo');
    }
    setConfig({ ...configWithoutLogo, customLogo: logo !== undefined ? logo : customLogo });
  }, [config]);

  return (
    <SystemConfigContext.Provider value={{ config, loading, saveConfig, reload: loadConfig }}>
      {children}
    </SystemConfigContext.Provider>
  );
}

export function useSystemConfig() {
  const ctx = useContext(SystemConfigContext);
  if (!ctx) return { config: DEFAULT_CONFIG, loading: false };
  return ctx;
}

export { DEFAULT_CONFIG };
